/**
 * AetherLite — catálogo de alimentos (público, no requiere auth).
 *
 * Backend: GET /aetherlite/foods?query=...&category=...
 * Devuelve ~196 alimentos curados LATAM con macros por `base_quantity` del
 * `base_unit` (típicamente por 100 g).
 *
 * El catálogo es chico, así que se carga UNA vez y se cachea en memoria; el
 * `FoodPickerModal` filtra/busca del lado del cliente.
 */
import { apiFetch } from "./client";

export interface Food {
  id: number;
  name: string;
  /** Unidad base de los macros — normalmente "g". */
  base_unit: string;
  /** Cantidad base de los macros — normalmente 100. */
  base_quantity: number;
  /** Macros POR `base_quantity` del `base_unit`. */
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  category: string | null;
  data_source: string | null;
}

export async function listFoods(query?: string, category?: string): Promise<Food[]> {
  const params = new URLSearchParams();
  if (query) params.set("query", query);
  if (category) params.set("category", category);
  const qs = params.toString();
  return apiFetch<Food[]>(qs ? `/aetherlite/foods?${qs}` : "/aetherlite/foods");
}

// ---- Cache en memoria del catálogo completo ----------------------------

let _cache: Food[] | null = null;
let _inflight: Promise<Food[]> | null = null;

/**
 * Carga el catálogo completo una sola vez y lo cachea. Llamadas concurrentes
 * comparten la misma promesa. Si falla, se puede reintentar (no cachea el error).
 */
export async function loadFoodCatalog(): Promise<Food[]> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = listFoods()
    .then((foods) => {
      _cache = foods;
      _inflight = null;
      return foods;
    })
    .catch((err) => {
      _inflight = null;
      throw err;
    });
  return _inflight;
}

/**
 * Invalida la cache del catálogo. Útil cuando el admin agrega/edita/borra
 * alimentos — la próxima `loadFoodCatalog()` re-fetchea desde el backend.
 */
export function invalidateFoodCatalog(): void {
  _cache = null;
  _inflight = null;
}
